import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAdminStore from '../stores/useAdminStore';
import LineChart from '../components/LineChart';
import BarChart from '../components/BarChart';
import exportToPDF from '../utils/exportToPDF';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';

const AdminDashboard = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    platformAnalytics,
    growthData,
    systemHealth,
    pendingCourses,
    fetchPlatformAnalytics,
    fetchGrowthData,
    fetchSystemHealth,
    pendingInstructors,
    fetchPendingInstructors,
    fetchPendingCourses,
    verifyInstructor,
    isLoading,
    error
  } = useAdminStore();

  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });



  // Rejection modal state
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    instructorId: null,
    comments: ''
  });

  useEffect(() => {
    fetchPlatformAnalytics();
    fetchSystemHealth();
    fetchGrowthData(dateRange.startDate, dateRange.endDate);
    fetchPendingCourses();
    fetchPendingInstructors();
  }, [dateRange, fetchGrowthData, fetchPendingCourses, fetchPlatformAnalytics, fetchSystemHealth, fetchPendingInstructors]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const exportAdminReport = () => {
    if (!platformAnalytics || !growthData) return;
    const columns = ['Metric', 'Value'];
    const rows = [
      ['Total Users', platformAnalytics?.totalUsers],
      ['Active Users', platformAnalytics?.activeUsers],
      ['Active Sessions', platformAnalytics?.activeSessions],
      ['Total Courses', platformAnalytics?.totalCourses],
      ['Approved Courses', platformAnalytics?.approvedCourses],
      ['Total Revenue', platformAnalytics?.totalRevenue.toLocaleString()],
      ['Account Balance', platformAnalytics?.paystackBalance.toLocaleString()],

    ];
    exportToPDF(platformAnalytics, 'Admin_Report', columns, rows);
  };

  const handleVerifyInstructor = async (instructorId, verified, comments = 'Registration Approved') => {
    try {
      await verifyInstructor(instructorId, verified, comments);
      fetchPendingInstructors();
      toast.success(`Instructor ${verified ? 'approved' : 'rejected'} successfully!`);
    } catch (error) {
      console.error('Error verifying instructor:', error);
      toast.error('Failed to verify instructor');
    }
  };

  const openRejectionModal = (instructorId) => {
    setRejectionModal({
      isOpen: true,
      instructorId,
      comments: ''
    });
  };

  const closeRejectionModal = () => {
    setRejectionModal({
      isOpen: false,
      instructorId: null,
      comments: ''
    });
  };

  const handleRejectSubmit = () => {
    if (!rejectionModal.comments.trim()) {
      toast.error('Please provide rejection comments.');
      return;
    }
    handleVerifyInstructor(rejectionModal.instructorId, false, rejectionModal.comments);
    closeRejectionModal();
  };

  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading dashboard data</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
            <button
              onClick={() => {
                fetchPlatformAnalytics();
                fetchSystemHealth();
                fetchGrowthData(dateRange.startDate, dateRange.endDate);
                fetchPendingCourses();
                fetchPendingInstructors();
              }}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Admin Dashboard</h1>
          <div className="flex justify-between mb-8">
            <div>
              <p className="text-neutral-600 dark:text-neutral-400">
                Monitor platform health, user growth, and system performance
              </p>
            </div>
            <button
              onClick={exportAdminReport}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Export Report
            </button>
          </div>

          {/* Date Range Picker */}
          <div className="mb-8 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Select Date Range</h3>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange({
                    ...dateRange,
                    startDate: new Date(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateRangeChange({
                    ...dateRange,
                    endDate: new Date(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    const now = new Date();
                    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    setDateRange({
                      startDate: thirtyDaysAgo,
                      endDate: now
                    });
                  }}
                  className="px-4 py-2 mt-6 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                >
                  Reset to 30 Days
                </button>
              </div>
            </div>
          </div>

          <div className='flex justify-end'>
            <Link
              to="/admin/cloud/course/builder"
              className="px-4 py-2  bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              + Create New Course
            </Link>
          </div>


          {/* Tabs */}
          <div className="mb-8 border-b border-neutral-200 dark:border-neutral-700">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'courses', 'settings'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* KPI Stats */}
              {platformAnalytics && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">👥</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Users</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          {platformAnalytics.totalUsers}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">👥</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Active Users</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          {platformAnalytics.activeUsers}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">🎓</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Courses</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          {platformAnalytics.totalCourses.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">📈</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Revenue</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          ₦{platformAnalytics.totalRevenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">💰</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Account Balance</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          ₦{platformAnalytics.paystackBalance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">📊</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Active Sessions</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          {platformAnalytics.activeSessions}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mr-4">
                        <span className="text-2xl">⏳</span>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">Approved Courses</p>
                        <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                          {platformAnalytics.approvedCourses}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructor Verification Queue */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Pending Instructor Verifications</h2>
                {pendingInstructors.filter(
                  instructor => instructor.onboarding?.adminVerified === false
                ).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                      <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Instructor</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Professional Title</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Experience</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Documents</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                        {pendingInstructors.filter(
                          instructor => instructor.onboarding?.adminVerified === false
                        ).map((instructor) => (
                          <tr key={instructor?._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-neutral-900 dark:text-white">{instructor?.name}</div>
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">{instructor?.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900 dark:text-white">{instructor?.professionalTitle}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900 dark:text-white">{instructor?.yearsOfExperience} years</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-2">
                                {instructor.cv && (
                                  <Link
                                    to={`${process.env.REACT_APP_SOCKET_URL}${instructor.cv}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 mr-3"
                                  >
                                    View CV
                                  </Link>
                                )}
                                {instructor.idCard && (
                                  <Link
                                    to={`${process.env.REACT_APP_SOCKET_URL}${instructor.idCard}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                                  >
                                    View ID Card
                                  </Link>
                                )}
                                {instructor.signedMoa && (
                                  <Link
                                    to={`${process.env.REACT_APP_SOCKET_URL}${instructor.signedMoa}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-pink-600 hover:text-pink-900 dark:text-pink-400 dark:hover:text-pink-300 mr-3"
                                  >
                                    View signed Moa
                                  </Link>
                                )}
                                {instructor.signedNda && (
                                  <Link
                                    to={`${process.env.REACT_APP_SOCKET_URL}${instructor.signedNda}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                  >
                                    View signed Nda
                                  </Link>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleVerifyInstructor(instructor._id, true, 'Registration Approved')}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-4"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => openRejectionModal(instructor._id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Reject
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">✅</div>
                    <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No pending instructors</h3>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      All instructors have been verified.
                    </p>
                  </div>
                )}
              </div>

              {/* System Health */}
              {systemHealth && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">System Health</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">CPU Usage</p>
                      <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-2">{systemHealth.cpu.usage.toFixed(1)}%</p>
                      <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mt-2">
                        <div
                          className={`h-2 rounded-full ${systemHealth.cpu.usage > 80 ? 'bg-red-500' : systemHealth.cpu.usage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                          style={{ width: `${systemHealth.cpu.usage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">Memory Usage</p>
                      <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-2">{systemHealth.memory.usage.toFixed(1)}%</p>
                      <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mt-2">
                        <div
                          className={`h-2 rounded-full ${systemHealth.memory.usage > 85 ? 'bg-red-500' : systemHealth.memory.usage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                          style={{ width: `${systemHealth.memory.usage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">Storage Usage</p>
                      <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-2">{systemHealth.storage.used.toFixed(1)}%</p>
                      <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mt-2">
                        <div
                          className={`h-2 rounded-full ${systemHealth.storage.used > 90 ? 'bg-red-500' : systemHealth.storage.used > 75 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                          style={{ width: `${systemHealth.storage.used}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">System Status</p>
                      <p className={`text-2xl font-bold mt-2 ${systemHealth.status === 'healthy' ? 'text-green-500' :
                        systemHealth.status === 'warning' ? 'text-yellow-500' : 'text-red-500'
                        }`}>
                        {systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* User Growth */}
              {growthData && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">User Growth</h2>
                  <LineChart
                    data={{
                      labels: growthData.userGrowth.map(item => item?._id),
                      datasets: [
                        {
                          label: 'New Users',
                          data: growthData.userGrowth.map(item => item.count),
                          borderColor: '#3b82f6',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          fill: true,
                          tension: 0.4,
                          pointRadius: 4,
                        }
                      ]
                    }}
                    height={300}
                  />

                </div>
              )}

              {/* Course Growth */}
              {growthData && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Course Growth</h2>
                  <BarChart
                    data={{
                      labels: growthData.courseGrowth.map(item => item?._id),
                      datasets: [
                        {
                          label: 'New Courses',
                          data: growthData.courseGrowth.map(item => item.count),
                          backgroundColor: '#10b981'
                        }
                      ]
                    }}
                    height={300}
                  />
                </div>
              )}
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Pending Course Approvals</h2>
              {pendingCourses && pendingCourses?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                    <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Course</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Instructor</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Category</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                      {pendingCourses?.map((course) => (
                        <tr key={course?._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-neutral-900 dark:text-white">{course.title}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-900 dark:text-white">{course?.instructor?.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-900 dark:text-white">{course?.category?.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${course?.status === 'pending'
                              ? 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300'
                              : course?.status === 'approved'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                              {course?.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => navigate(`/admin/cloud/courses`)}
                              className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 mr-4"
                            >
                              manage
                            </button>

                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">✅</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No pending courses</h3>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    All courses have been approved or rejected.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">System Settings</h2>
              <div className="space-y-6">
                <div className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">General Settings</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Configure site name, URL, and basic settings.</p>
                  <button
                    onClick={() => navigate('/admin/cloud/settings')}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Edit Settings
                  </button>
                </div>
                <div className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">Email Settings</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Configure SMTP settings for email notifications.</p>
                  <button
                    onClick={() => navigate('/admin/cloud/settings')}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Edit Settings
                  </button>
                </div>
                <div className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">Payment Settings</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Configure currency, tax rate, and payment gateway.</p>
                  <button
                    onClick={() => navigate('/admin/cloud/settings')}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Edit Settings
                  </button>
                </div>
                <div className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">Security Settings</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Configure password policies and security settings.</p>
                  <button
                    onClick={() => navigate('/admin/cloud/settings')}
                    className="mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Edit Settings
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Comment Modal */}
      {rejectionModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-900 opacity-75" onClick={closeRejectionModal}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div
              className="inline-block align-bottom bg-white dark:bg-neutral-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
            >
              <div className="bg-white dark:bg-neutral-800 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Reject Instructor</h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  Please provide detailed feedback to help the instructor improve their application.
                </p>
                <textarea
                  value={rejectionModal.comments}
                  onChange={(e) => setRejectionModal({ ...rejectionModal, comments: e.target.value })}
                  rows={4}
                  placeholder="Enter your comments here..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-700/50 px-6 py-4 flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  onClick={closeRejectionModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRejectSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Reject Instructor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminDashboard;